import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DropdownItemBase } from '@/components/Autocomplete/autocomplete.types';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import type { DemographicsSectionValuesIn } from '@/features/DataChangeRequest/_schemas/HcpSchemas/DemographicsSection.validation';
import { buildYearOptions } from '@/features/DataChangeRequest/_utils/buildYearOptions';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import { FormSection } from '@/features/DataChangeRequest/DcrModal/FormSection/FormSection';

type Option = Pick<DropdownItemBase, 'id' | 'name'>;

export const HcpDemographicsSection = () => {
	const { t } = useTranslation();
	const { hcpFields } = useDcrFields();
	const {
		control,
		setValue,
		formState: { dirtyFields }
	} = useFormContext<DemographicsSectionValuesIn>();
	const section = 'demographics';

	const {
		picklists: { hcpGenderOptions /* languageOptions */ }
	} = useDcrContext();

	const yearOptions = useMemo(() => buildYearOptions<Option>(), []);

	return (
		<FormSection title={t('Demographics')} id={section}>
			{/* Note(Filip): As of 27.01.2026 - Jonas reqested this to be hidden for the time being */}
			{/* <DcrFormController
				name='languageCda_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<Option>
						label={t('Language')}
						id='languageCda_V'
						name={field.name}
						options={languageOptions ?? []}
						defaultValue={(field.value ?? '') as string}
						indicateError={fieldState.error ? true : false}
						isDirty={dirtyFields.languageCda_V}
						handleOnDropdownItemSelect={item => {
							const option = languageOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/> */}

			<DcrFormController
				name='gender_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<Option>
						label={hcpFields.gender_V.label}
						id='gender_V'
						name={field.name}
						section={section}
						options={hcpGenderOptions ?? []}
						defaultValue={(field.value ?? '') as string}
						indicateError={fieldState.error ? true : false}
						isDirty={dirtyFields.gender_V}
						handleOnDropdownItemSelect={item => {
							const option = hcpGenderOptions?.find(opt => opt.id === item?.id);
							field.onChange(option?.name ?? item?.id ?? '');
						}}
					/>
				)}
			/>

			<DcrFormController
				name='birthYear_V'
				section={section}
				control={control}
				render={({ field, fieldState }) => (
					<FormAutocompleteField<Option>
						label={hcpFields.birthYear_V.label}
						id='birthYear_V'
						name={field.name}
						section={section}
						options={yearOptions}
						defaultValue={
							typeof field.value === 'number' ? String(field.value) : ''
						}
						indicateError={fieldState.error ? true : false}
						isDirty={dirtyFields.birthYear_V}
						handleOnDropdownItemSelect={item => {
							const n = Number(item?.id);
							if (Number.isNaN(n)) {
								// If cleared or invalid, unset the value
								setValue('birthYear_V', undefined, {
									shouldDirty: true,
									shouldValidate: true
								});
							} else {
								field.onChange(n);
							}
						}}
					/>
				)}
			/>

			{/* Note(Filip): As of 15.12.2025 - temporarily hidden as a request by Jonas */}
			{/* <DcrFormController
				name='dataPrivacyOptOut_V'
				section={section}
				control={control}
				render={({ field }) => (
					<FormAutocompleteField
						label='OpenData Data Privacy Opt Out'
						id='dataPrivacyOptOut_V'
						isDirty={dirtyFields.dataPrivacyOptOut_V}
						name={field.name}
						options={yesOrNowOptions}
						defaultValue={field.value ? 'yes' : 'no'}
						handleOnDropdownItemSelect={item =>
							field.onChange(item?.id === 'yes')
						}
					/>
				)}
			/> */}
		</FormSection>
	);
};
